/* eslint-disable max-lines */
import "./contactus-confirmation-modal.scss";
import { on as onEvent } from "common/utils/ui/event";
import { generateTimeRemainingText } from "common/utils/ui/generate-time-remaining";

const submitFormRequest = url => {
  // Create and submit a form to handle the POST request
  const $form = document.createElement("form");
  $form.method = "POST";
  $form.action = url;
  $form.style.display = "none";

  // Add CSRF token
  const $csrfToken = document.querySelector('meta[name="csrf-token"]');
  if ($csrfToken) {
    const $csrfInput = document.createElement("input");
    $csrfInput.type = "hidden";
    $csrfInput.name = "authenticity_token";
    $csrfInput.value = $csrfToken.getAttribute("content");
    $form.appendChild($csrfInput);
  }

  document.body.appendChild($form);
  $form.submit();
  cleanUpForm($form);
};

// Build API URL with order number
const buildDeliveryApiUrl = orderNumber => {
  const url = new URL("/api/v2/cart/delivery.json", window.location.origin);
  if (orderNumber) {
    url.searchParams.append("order_number", orderNumber);
    url.searchParams.append("completed_order", true);
  }
  return url.toString();
};

// Extract earliest valid until time from API response
/* eslint-disable max-lines-per-function */
const extractEarliestValidUntilTime = data => {
  let earliestValidUntilTime = null;

  // First check if there's a top-level valid_until_time
  if (data && data.valid_until_time) {
    const topLevelTime = new Date(data.valid_until_time).getTime();
    if (!Number.isNaN(topLevelTime)) {
      earliestValidUntilTime = topLevelTime;
    }
  }

  // Check for valid_until_time in delivery options
  if (data && data.delivery && data.delivery.sub_orders) {
    Object.values(data.delivery.sub_orders).forEach(subOrder => {
      if (subOrder.delivery_options) {
        subOrder.delivery_options.forEach(option => {
          if (option.valid_until_time) {
            const validTime = new Date(option.valid_until_time).getTime();
            if (
              !Number.isNaN(validTime) &&
              (!earliestValidUntilTime || validTime < earliestValidUntilTime)
            ) {
              earliestValidUntilTime = validTime;
            }
          }
        });
      }
    });
  }
  /* eslint-enable max-lines-per-function */

  // Also check in shipments if available
  if (data && data.shipments && data.shipments.orders) {
    // Iterate through all orders to find the earliest valid_until_time
    Object.values(data.shipments.orders).forEach(order => {
      if (order.options) {
        order.options.forEach(option => {
          // Check if this option is selected and has a valid_until_time
          if (option.selected && option.valid_until_time) {
            const validTime = new Date(option.valid_until_time).getTime();
            if (
              !Number.isNaN(validTime) &&
              (!earliestValidUntilTime || validTime < earliestValidUntilTime)
            ) {
              earliestValidUntilTime = validTime;
            }
          }
        });
      }
    });
  }

  return earliestValidUntilTime;
};

// Function to update the countdown display
const updateCountdown = ($modal, validUntilTimes) => {
  const validUntilTime = validUntilTimes.get($modal);

  if (!validUntilTime) {
    return;
  }

  const timeRemaining = generateTimeRemainingText(validUntilTime);

  const $timeRemainingContainer = $modal.querySelector(".js-time-remaining-container");
  const $timeRemainingValue = $modal.querySelector(".js-time-remaining-value");

  if ($timeRemainingContainer && $timeRemainingValue) {
    // Always update the text content to avoid stale values
    $timeRemainingValue.textContent = timeRemaining || "";

    $timeRemainingContainer.classList.toggle("hidden", !timeRemaining); // Show/hide the container based on the time remaining
  }
};

// Setup countdown interval
/* eslint-disable max-params */
const setupCountdownInterval = ($modal, validUntilTime, validUntilTimes, countdownIntervals) => {
  validUntilTimes.set($modal, validUntilTime);
  updateCountdown($modal, validUntilTimes);

  // Clear any existing interval for this modal
  const existingInterval = countdownIntervals.get($modal);
  if (existingInterval) {
    clearInterval(existingInterval);
  }

  // Update every minute (60000ms)
  const interval = setInterval(() => {
    updateCountdown($modal, validUntilTimes);
  }, 60000);
  countdownIntervals.set($modal, interval);
};
/* eslint-enable max-params */

// Function to fetch delivery data
const fetchDeliveryData = async ($modal, validUntilTimes, countdownIntervals) => {
  try {
    // Get order number from modal data attribute
    const { orderNumber } = $modal.dataset;

    const response = await fetch(buildDeliveryApiUrl(orderNumber), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      credentials: "include"
    });

    if (!response.ok) {
      console.warn(`Failed to fetch delivery data for order ${orderNumber}: ${response.status}`);

      return;
    }

    const data = await response.json();
    const earliestValidUntilTime = extractEarliestValidUntilTime(data);

    if (earliestValidUntilTime) {
      setupCountdownInterval($modal, earliestValidUntilTime, validUntilTimes, countdownIntervals);
    }
  } catch (error) {
    // Keep UI visible on API error
    console.error("Error fetching delivery data:", error);
  }
};

// Function to handle modal show
/* eslint-disable max-params */
const handleModalShow = ($modal, activeModals, validUntilTimes, countdownIntervals) => {
  // Prevent duplicate fetching
  if (activeModals.has($modal)) return;
  activeModals.add($modal);

  // Add a small delay to ensure modal content is fully rendered
  setTimeout(() => {
    // Fetch real delivery data from API
    fetchDeliveryData($modal, validUntilTimes, countdownIntervals);
  }, 100);
};
/* eslint-enable max-params */

// Function to handle modal hide
/* eslint-disable max-params */
const handleModalHide = ($modal, activeModals, validUntilTimes, countdownIntervals) => {
  activeModals.delete($modal);

  // Clear interval for this modal
  const interval = countdownIntervals.get($modal);
  if (interval) {
    clearInterval(interval);
    countdownIntervals.delete($modal);
  }

  // Clear stored time
  validUntilTimes.delete($modal);
};
/* eslint-enable max-params */

// Function to set up event listeners for a modal
/* eslint-disable max-params */
const setupModalListeners = ($modal, activeModals, validUntilTimes, countdownIntervals) => {
  // Listen for modal show event
  $modal.addEventListener("shown.bs.modal", () =>
    handleModalShow($modal, activeModals, validUntilTimes, countdownIntervals)
  );
  $modal.addEventListener("show.bs.modal", () =>
    handleModalShow($modal, activeModals, validUntilTimes, countdownIntervals)
  );

  // Listen for modal hide event
  $modal.addEventListener("hidden.bs.modal", () =>
    handleModalHide($modal, activeModals, validUntilTimes, countdownIntervals)
  );
  $modal.addEventListener("hide.bs.modal", () =>
    handleModalHide($modal, activeModals, validUntilTimes, countdownIntervals)
  );

  // jQuery fallback if available
  if (typeof $ !== "undefined" && $.fn && $.fn.modal) {
    const $modalJQ = $($modal);
    $modalJQ.on("shown.bs.modal show.bs.modal", () =>
      handleModalShow($modal, activeModals, validUntilTimes, countdownIntervals)
    );
    $modalJQ.on("hidden.bs.modal hide.bs.modal", () =>
      handleModalHide($modal, activeModals, validUntilTimes, countdownIntervals)
    );
  }

  // Also check if modal is already visible
  if ($modal.classList.contains("show") || $modal.style.display === "block") {
    handleModalShow($modal, activeModals, validUntilTimes, countdownIntervals);
  }
};
/* eslint-enable max-params */

// Setup modal trigger listeners
/* eslint-disable max-params */
const setupModalTriggers = (activeModals, validUntilTimes, countdownIntervals) => {
  const $modalTriggers = document.querySelectorAll(
    '[data-toggle="modal"][data-target*="contactus-confirmation-modal"], [data-bs-toggle="modal"][data-bs-target*="contactus-confirmation-modal"]'
  );

  $modalTriggers.forEach($trigger => {
    $trigger.addEventListener("click", () => {
      const targetSelector =
        $trigger.getAttribute("data-target") || $trigger.getAttribute("data-bs-target");
      if (targetSelector) {
        const $targetModal = document.querySelector(targetSelector);
        if ($targetModal && $targetModal.classList.contains("js-contactus-confirmation-modal")) {
          // Give modal time to start opening
          setTimeout(
            () => handleModalShow($targetModal, activeModals, validUntilTimes, countdownIntervals),
            100
          );
        }
      }
    });
  });
};

// Initialize event listeners for all modals
const initializeModals = (activeModals, validUntilTimes, countdownIntervals) => {
  const $modals = document.querySelectorAll(".js-contactus-confirmation-modal");

  $modals.forEach($modal => {
    setupModalListeners($modal, activeModals, validUntilTimes, countdownIntervals);
  });

  // Also listen for clicks on modal triggers
  setupModalTriggers(activeModals, validUntilTimes, countdownIntervals);
};

// Setup mutation observer for dynamically added modals
const setupMutationObserver = (activeModals, validUntilTimes, countdownIntervals) => {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach($node => {
        if ($node.nodeType === 1) {
          // Element node
          if ($node.classList && $node.classList.contains("js-contactus-confirmation-modal")) {
            setupModalListeners($node, activeModals, validUntilTimes, countdownIntervals);
          }
          // Also check descendants
          const $modals = $node.querySelectorAll(".js-contactus-confirmation-modal");
          $modals.forEach($modal => {
            setupModalListeners($modal, activeModals, validUntilTimes, countdownIntervals);
          });
        }
      });
    });
  });

  // Start observing the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};

// Handle cancel order link click
const handleCancelOrderClick = (event, $link) => {
  // Prevent default to handle the loading state first
  event.preventDefault();

  // Check if already loading
  if ($link.classList.contains("is-loading")) return;

  // Add loading state
  $link.classList.add("is-loading");

  // Hide text and show loader
  const $textElement = $link.querySelector(".js-cancel-order-text");
  const $loaderElement = $link.querySelector(".js-cancel-order-loader");

  if ($textElement) $textElement.style.display = "none";
  if ($loaderElement) $loaderElement.style.display = "inline-flex";

  // Disable the link
  $link.style.pointerEvents = "none";
  $link.style.opacity = "0.7";

  // Submit the form after a short delay to show the loading state
  setTimeout(() => {
    submitFormRequest($link.href);
  }, 100);
};

const cleanUpForm = $form => {
  requestAnimationFrame(() => $form.remove());
};

const initContactusConfirmationModal = () => {
  const countdownIntervals = new WeakMap();
  const validUntilTimes = new WeakMap();
  const activeModals = new WeakSet();

  // Initialize everything
  // Ensure DOM is ready before initializing
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initializeModals(activeModals, validUntilTimes, countdownIntervals);
    });
  } else {
    // DOM is already ready
    initializeModals(activeModals, validUntilTimes, countdownIntervals);
  }

  // Setup mutation observer
  setupMutationObserver(activeModals, validUntilTimes, countdownIntervals);

  // Handle click event on cancel order links
  onEvent("click", ".js-cancel-order-link", handleCancelOrderClick);
};

// Export globally for use in other modules
window.initContactusConfirmationModal = initContactusConfirmationModal;

export default initContactusConfirmationModal;
